import { useCallback, useRef } from "react";
import { createChildLogger } from "@retreat/shared";
import type { EngineSnapshot } from "@retreats/engine";
import type { UIAction } from "../bridge/types";
import type { RetreatManager } from "@retreats/engine";
import { RetreatState } from "@retreats/engine";
import type { EditableRetreatSchedule } from "@retreat/core";
import type { IntentionRecord } from "@retreats/content";
import type { AudioPlayer } from "../bridge/audioPlayer";
import { TabCoordinator } from "../services/TabCoordinator";
import { reportError } from "../utils/reportError";
import { PushAlarmService } from "../services/PushAlarmService";
import { loadWakeOffset } from "../state/schedulingState";
import { STORAGE_KEYS, WAKE_OFFSET_MS } from "../constants";
import { getDefaultBell } from "../state/bellState";

// Maps content-package tradition labels (from metadata.tradition) to BellLibrary TraditionIds.
export const CONTENT_TO_BELL_TRADITION: Record<string, string> = {
  zen: 'soto_zen',
  vipassana: 'vipassana_goenka',
};

export function resolveTraditionId(raw?: string): string | null {
  if (!raw) return null;
  return CONTENT_TO_BELL_TRADITION[raw.toLowerCase()] ?? null;
}

function isRetreatAlreadyActiveLocally(status: string, coordinatorActive: boolean): boolean {
  return status === RetreatState.RUNNING || coordinatorActive;
}

const log = createChildLogger("useRetreatHandlers");

export function useRetreatHandlers(
  snapshot: EngineSnapshot,
  audioPlayer: AudioPlayer,
  dispatchUI: React.Dispatch<UIAction>,
  manager: RetreatManager,
  selectedSchedule: EditableRetreatSchedule | null = null,
  preparationSeen: boolean = false,
  intentionAction: null | 'now' | 'scheduled' = null,
  getTTSEnabled: () => boolean = () => false,
) {
  const _resumeAudio = useCallback(async (): Promise<void> => {
    await audioPlayer.resume();
  }, [audioPlayer]);

  const stopAndComplete = useCallback(
    async (onError?: () => void) => {
      try {
        // Capture elapsed BEFORE stop() — engine resets to 0 on stop
        const elapsedSeconds = manager.getSnapshot().elapsedSeconds;
        await manager.stop();
        audioPlayer.clearTradition();
        TabCoordinator.get().setInactive();
        dispatchUI({ type: "SHOW_COMPLETION", elapsedSeconds });
      } catch (err) {
        reportError(err, "RETREAT_STOP_FAILED");
        log.info("Error stopping retreat: %s", String(err));
        onError?.();
      }
    },
    [manager, audioPlayer, dispatchUI],
  );

  const handlePermissionsGranted = useCallback(() => {
    audioPlayer
      .resume()
      .catch((err) => log.info("Audio resume failed: %s", err));
    // Request notification permission so Web Push alarms can fire (scheduled retreat wake bells).
    // iOS requires the app to be installed (standalone) before PushManager works, but we ask
    // here regardless — on iOS it silently no-ops until installed; on Android it shows the prompt.
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().then((result) => {
        log.info("Notification permission result", { result });
      }).catch(() => {});
    }
    dispatchUI({ type: "PERMISSIONS_GRANTED" });
  }, [audioPlayer, dispatchUI]);

  const handlePermissionsSkip = useCallback(() => {
    // M2-UX-P0-003: Do NOT call audioPlane.resume() on skip.
    // Do NOT mark permissions as granted. User sees PermissionsScreen again next launch.
    dispatchUI({ type: "PERMISSIONS_DISMISSED" });
  }, [dispatchUI]);

  // Guard against double-start: dispatching PREPARATION_SEEN triggers a re-render
  // while manager.start() is still awaited, which re-fires the auto-start effect.
  const startingRef = useRef(false);
  const handlePreparationContinue = useCallback(async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    dispatchUI({ type: "PREPARATION_SEEN" });
    try {
      await _resumeAudio();
      await manager.start();
    } catch (err) {
      startingRef.current = false;
      reportError(err, "RETREAT_START_FAILED");
      log.info("Error starting retreat: %s", String(err));
    }
  }, [_resumeAudio, manager, dispatchUI]);

  // Dismisses the ReturningScreen after auto-resume (user taps or timer fires).
  // Attempts audio unlock — succeeds when triggered by a tap gesture.
  const handleReturningDismiss = useCallback(async () => {
    await _resumeAudio().catch(() => {});
    dispatchUI({ type: "RETURNING_COMPLETE" });
  }, [_resumeAudio, dispatchUI]);

  // Takes over engine ownership from another browser tab.
  const handleReturningTakeover = useCallback(async () => {
    if (!snapshot.pendingSession) return;
    try {
      await _resumeAudio();
      await manager.confirmResume(snapshot.pendingSession);
      TabCoordinator.get().setActive(snapshot.pendingSession.retreatId);
      dispatchUI({ type: "OTHER_TAB_TAKEOVER" });
      dispatchUI({ type: "RETURNING_COMPLETE" });
    } catch (err) {
      reportError(err, "RETREAT_TAKEOVER_FAILED");
      log.info("Error taking over retreat: %s", String(err));
    }
  }, [snapshot.pendingSession, _resumeAudio, manager, dispatchUI]);

  // Ends the retreat from the resume card or returning screen.
  // RUNNING: engine is live in this tab — stop directly.
  // PROBED (pendingSession set, status IDLE): resume to RUNNING then immediately stop + complete.
  // Other-tab mode (otherTabActive, no pendingSession): just dismiss to home.
  const handleReturningEnd = useCallback(async () => {
    if (snapshot.status === RetreatState.RUNNING) {
      await stopAndComplete();
    } else if (snapshot.pendingSession) {
      try {
        await _resumeAudio().catch(() => {});
        await manager.confirmResume(snapshot.pendingSession);
        await stopAndComplete();
      } catch {
        dispatchUI({ type: "RETURNING_COMPLETE" });
      }
    } else {
      dispatchUI({ type: "RETURNING_COMPLETE" });
    }
  }, [snapshot.status, snapshot.pendingSession, stopAndComplete, _resumeAudio, manager, dispatchUI]);

  const handleHearBell = useCallback(() => {
    audioPlayer.playInstrumentPreview(getDefaultBell());
  }, [audioPlayer]);

  const handleLiveEndRequest = useCallback(
    () => stopAndComplete(),
    [stopAndComplete],
  );

  const handleEndConfirm = useCallback(
    () => stopAndComplete(() => dispatchUI({ type: "CANCEL_END" })),
    [stopAndComplete, dispatchUI],
  );

  const handleEndCancel = useCallback(() => {
    dispatchUI({ type: "CANCEL_END" });
  }, [dispatchUI]);

  const handleCompletionReturnHome = useCallback(() => {
    dispatchUI({ type: "DISMISS_COMPLETION" });
  }, [dispatchUI]);

  // ── Scheduling flow handlers (moved from RetreatBridge for SRP) ─────────

  const handleOpenLibrary = useCallback(() => {
    dispatchUI({ type: "OPEN_LIBRARY" });
  }, [dispatchUI]);

  const handleOpenSelection = useCallback(() => {
    dispatchUI({ type: "OPEN_SELECTION" });
  }, [dispatchUI]);

  const handleViewRetreatDetail = useCallback(
    (schedule: EditableRetreatSchedule) => {
      try { localStorage.setItem(STORAGE_KEYS.LAST_SCREEN, 'retreat-detail'); } catch { /* quota */ }
      dispatchUI({ type: "VIEW_RETREAT_DETAIL", schedule, origin: "preset", baseScheduleId: null });
    },
    [dispatchUI],
  );

  const handleCloseLibrary = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEYS.LAST_SCREEN, 'home'); } catch { /* quota */ }
    dispatchUI({ type: "CLOSE_LIBRARY" });
  }, [dispatchUI]);

  const handleSelectRetreat = useCallback(
    (schedule: EditableRetreatSchedule) => {
      dispatchUI({ type: "SELECT_RETREAT", schedule });
    },
    [dispatchUI],
  );

  const handleConfirmSchedule = useCallback(
    (
      scheduledAt: number,
      wakeOffset?: import("../constants").WakeOffset,
      generatedSchedule?: EditableRetreatSchedule,
    ) => {
      const scheduleToUse = generatedSchedule ?? selectedSchedule;
      if (scheduleToUse) {
        manager.selectRetreat(scheduleToUse);
        manager
          .probe()
          .catch((err) => log.info("Probe after schedule: %s", String(err)));
      }
      dispatchUI({ type: "CONFIRM_SCHEDULE", scheduledAt, wakeOffset, generatedSchedule });

      // Arm server-side push (online enhancement — gracefully skipped if offline,
      // unsupported platform, or wakeOffset === 'self').
      const offset = wakeOffset ?? loadWakeOffset();
      if (offset !== 'self' && scheduleToUse) {
        const wakeOffsetMs = WAKE_OFFSET_MS[offset] ?? WAKE_OFFSET_MS['30min'];
        PushAlarmService.register(
          scheduledAt,
          wakeOffsetMs,
          scheduleToUse.name,
        )
          .then((confirmed) => dispatchUI({ type: "PUSH_ALARM_REGISTRATION_RESULT", confirmed }))
          .catch((err) => log.info("Push registration failed: %s", String(err)));
      }
    },
    [selectedSchedule, manager, dispatchUI],
  );

  const handleCancelScheduling = useCallback(() => {
    dispatchUI({ type: "CANCEL_SCHEDULING" });
  }, [dispatchUI]);

  const handleCancelScheduledRetreat = useCallback(() => {
    // Read scheduledAt from state before the reducer clears it
    const scheduledAt = snapshot.pendingSession
      ? null // retreat already started — no alarm to cancel
      : (() => {
          try {
            const raw = localStorage.getItem(STORAGE_KEYS.SCHEDULED_DATETIME);
            return raw ? Number(raw) : null;
          } catch { return null; }
        })();

    dispatchUI({ type: "CANCEL_SCHEDULED_RETREAT" });

    if (scheduledAt) {
      PushAlarmService.cancel(scheduledAt).catch((err) =>
        log.info("Push cancel failed: %s", String(err)),
      );
    }
  }, [dispatchUI, snapshot.pendingSession]);

  const handleBeginScheduledRetreat = useCallback(async () => {
    dispatchUI({ type: "BEGIN_SCHEDULED_RETREAT" });
    dispatchUI({ type: "BEGIN_RETREAT", pendingAction: "scheduled" });
    // Auto-decline intention — the user chose this retreat deliberately when scheduling.
    // DECLINE_INTENTION also clears rc_intention_pending from localStorage,
    // preventing a crash-recovery loop if the app reboots mid-sequence.
    dispatchUI({ type: "DECLINE_INTENTION" });
    try {
      const traditionId = resolveTraditionId(selectedSchedule?.metadata?.tradition);
      if (traditionId) audioPlayer.setTradition(traditionId);
      await _resumeAudio();
      // initialize() → INITIALIZING, start() → RUNNING + acquires screen wake lock.
      // start() internally calls initialize() if still IDLE, so the sequence is safe
      // even if initialize() is called first.
      await manager.initialize();
      await manager.start();
      TabCoordinator.get().setActive(selectedSchedule?.id ?? "retreat");
      manager.getSystemAlertManager().setEnabled(getTTSEnabled());
    } catch (err) {
      reportError(err, "RETREAT_INIT_FAILED");
      log.info("Error initializing scheduled retreat: %s", String(err));
    }
  }, [dispatchUI, audioPlayer, _resumeAudio, manager, selectedSchedule]);

  const handleScheduleFromDetail = useCallback(() => {
    dispatchUI({ type: "SCHEDULE_FROM_DETAIL" });
  }, [dispatchUI]);

  const handleStartNow = useCallback(
    async (schedule: EditableRetreatSchedule) => {
      if (isRetreatAlreadyActiveLocally(snapshot.status, TabCoordinator.get().isActive)) {
        log.info("handleStartNow: blocked — retreat already active locally");
        return;
      }
      try {
        manager.selectRetreat(schedule);
        await manager.probe();
        // After probe loads IDB state, check if another tab has ownership.
        const activeInOtherTab = await TabCoordinator.get().queryOtherTabs();
        if (activeInOtherTab) {
          log.info("handleStartNow: blocked — active retreat in another tab");
          dispatchUI({ type: "OTHER_TAB_ACTIVE" });
          return;
        }
        dispatchUI({ type: "BEGIN_RETREAT", selectedSchedule: schedule, pendingAction: "now" });
        // manager.initialize() is called by handleIntentionComplete
      } catch (err) {
        reportError(err, "RETREAT_PROBE_FAILED");
        log.info("Error during probe: %s", String(err));
      }
    },
    [manager, dispatchUI, snapshot.status],
  );

  const handleIntentionComplete = useCallback(
    async (record: IntentionRecord) => {
      // iOS: warm up speech synthesis synchronously before any await (must be in gesture handler)
      manager.getSystemAlertManager().warmUp();
      dispatchUI({ type: "SET_INTENTION", record });
      try {
        const traditionId = resolveTraditionId(selectedSchedule?.metadata?.tradition);
        if (traditionId) audioPlayer.setTradition(traditionId);
        await _resumeAudio();
        await manager.initialize();
        TabCoordinator.get().setActive(selectedSchedule?.id ?? "retreat");
        manager.getSystemAlertManager().setEnabled(getTTSEnabled());
        // For direct start: skip preparation screen if already seen.
        // For scheduled start: initialize() puts engine to RUNNING via pendingSession — no start() needed.
        if (intentionAction === 'now' && preparationSeen) {
          await manager.start();
        }
      } catch (err) {
        reportError(err, "RETREAT_INIT_FAILED");
        log.info("Error initializing retreat: %s", String(err));
      }
    },
    [manager, audioPlayer, dispatchUI, _resumeAudio, preparationSeen, selectedSchedule, intentionAction],
  );

  // Mirrors handleIntentionComplete but dispatches DECLINE_INTENTION instead of SET_INTENTION.
  // Must still call manager.initialize() so manager.start() in handlePreparationContinue succeeds.
  const handleIntentionDecline = useCallback(async () => {
    manager.getSystemAlertManager().warmUp();
    dispatchUI({ type: "DECLINE_INTENTION" });
    try {
      const traditionId = resolveTraditionId(selectedSchedule?.metadata?.tradition);
      if (traditionId) audioPlayer.setTradition(traditionId);
      await _resumeAudio();
      await manager.initialize();
      TabCoordinator.get().setActive(selectedSchedule?.id ?? "retreat");
      manager.getSystemAlertManager().setEnabled(getTTSEnabled());
      if (intentionAction === 'now' && preparationSeen) {
        await manager.start();
      }
    } catch (err) {
      reportError(err, "RETREAT_INIT_FAILED");
      log.info("Error initializing after intention decline: %s", String(err));
    }
  }, [manager, dispatchUI, _resumeAudio, preparationSeen, selectedSchedule, intentionAction]);

  return {
    handlePermissionsGranted,
    handlePermissionsSkip,
    handlePreparationContinue,
    handleReturningDismiss,
    handleReturningTakeover,
    handleReturningEnd,
    handleHearBell,
    handleLiveEndRequest,
    handleEndConfirm,
    handleEndCancel,
    handleCompletionReturnHome,
    handleOpenLibrary,
    handleOpenSelection,
    handleViewRetreatDetail,
    handleCloseLibrary,
    handleSelectRetreat,
    handleConfirmSchedule,
    handleCancelScheduling,
    handleCancelScheduledRetreat,
    handleBeginScheduledRetreat,
    handleScheduleFromDetail,
    handleStartNow,
    handleIntentionComplete,
    handleIntentionDecline,
  };
}
